import { useEffect, useSyncExternalStore } from "react";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/stores/auth";

/** The state the visitor is shopping in: their own pick, else their profile's state, else a guess from the IP. */
export interface MyState {
  id: number;
  name: string;
  source: "picked" | "auto";
}

const KEY = "rs:state";
const listeners = new Set<() => void>();
let cache: MyState | null | undefined;

function read(): MyState | null {
  if (cache !== undefined) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    cache = raw ? (JSON.parse(raw) as MyState) : null;
  } catch {
    cache = null;
  }
  return cache;
}

function write(value: MyState | null) {
  cache = value;
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: kept for this visit */
  }
  listeners.forEach((l) => l());
}

export function pickState(state: { id: number; name: string } | null) {
  write(state ? { id: state.id, name: state.name, source: "picked" } : null);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

let detecting = false;

/** Current shopping state (null = all of Nigeria). Detects once by IP when nothing is set. */
export function useMyState(): MyState | null {
  const value = useSyncExternalStore(subscribe, read, () => null);
  const profileState = useAuth((s) => s.user?.default_state ?? null);
  const status = useAuth((s) => s.status);
  useEffect(() => {
    if (value || detecting || status === "idle" || status === "loading") return;
    if (profileState) return; // the profile's state is used below without storing it
    detecting = true;
    void unwrap(api.GET("/api/v1/locations/states/detect/"))
      .then((res) => {
        if (res.state && !read()) write({ id: res.state.id, name: res.state.name, source: "auto" });
      })
      .catch(() => undefined);
  }, [value, profileState, status]);
  return value;
}

/** The state id to filter by: explicit pick → auto/profile. */
export function useShoppingStateId(): number | null {
  const mine = useMyState();
  const profileState = useAuth((s) => s.user?.default_state ?? null);
  return mine?.id ?? profileState ?? null;
}
